const normalizeString = (value) => String(value || '').trim();

export const normalizeClientName = (value) =>
  normalizeString(value)
    .replace(/\s+/g, ' ')
    .trim();

export const getClientDisplayName = (client = {}) => {
  const directName = normalizeClientName(client.name);
  if (directName) return directName;

  const firstName = normalizeString(client.firstName);
  const lastName = normalizeString(client.lastName);
  const combined = `${firstName} ${lastName}`.trim();
  return normalizeClientName(combined);
};

export const normalizeClientEmail = (value) => normalizeString(value).toLowerCase();

export const normalizeClientPhone = (value) =>
  normalizeString(value)
    .replace(/[^\d+]/g, '')
    .replace(/(?!^)\+/g, '');

export const normalizeClientTags = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(value.map((entry) => normalizeString(entry)).filter(Boolean))];
  }

  return [...new Set(
    String(value || '')
      .split(/[;,]/)
      .map((entry) => normalizeString(entry))
      .filter(Boolean)
  )];
};

export const createClientId = () =>
  `client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const getClientMatchKey = (client = {}) => {
  const email = normalizeClientEmail(client.email);
  if (email) return `email:${email}`;

  const phone = normalizeClientPhone(client.phone);
  if (phone) return `phone:${phone}`;

  const name = getClientDisplayName(client).toLowerCase();
  if (name) return `name:${name}`;

  return `id:${normalizeString(client.id)}`;
};

export const normalizeClientRecord = (client = {}, options = {}) => {
  const now = options.now || new Date().toISOString();
  const firstName = normalizeString(client.firstName);
  const lastName = normalizeString(client.lastName);
  const directName = normalizeClientName(client.name);
  const name = directName || normalizeClientName(`${firstName} ${lastName}`);
  const tags = normalizeClientTags(client.tags);

  return {
    id: normalizeString(client.id) || createClientId(),
    name,
    firstName,
    lastName,
    email: normalizeClientEmail(client.email),
    phone: normalizeClientPhone(client.phone),
    notes: normalizeString(client.notes),
    tags,
    createdAt: client.createdAt || now,
    updatedAt: client.updatedAt || now
  };
};

export const mergeClientLists = (existingClients = [], incomingClients = []) => {
  const merged = new Map();

  const addClient = (rawClient) => {
    const normalized = normalizeClientRecord(rawClient);
    if (!normalized.name && !normalized.email && !normalized.phone) return;

    const key = getClientMatchKey(normalized);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, normalized);
      return;
    }

    merged.set(key, {
      ...existing,
      ...normalized,
      id: existing.id || normalized.id,
      name: normalized.name || existing.name,
      firstName: normalized.firstName || existing.firstName,
      lastName: normalized.lastName || existing.lastName,
      email: normalized.email || existing.email,
      phone: normalized.phone || existing.phone,
      notes: normalized.notes || existing.notes,
      tags: [...new Set([...(existing.tags || []), ...(normalized.tags || [])])],
      createdAt: existing.createdAt || normalized.createdAt,
      updatedAt: normalized.updatedAt || existing.updatedAt
    });
  };

  (Array.isArray(existingClients) ? existingClients : []).forEach(addClient);
  (Array.isArray(incomingClients) ? incomingClients : []).forEach(addClient);

  return [...merged.values()].sort((a, b) =>
    getClientDisplayName(a).localeCompare(getClientDisplayName(b), undefined, { sensitivity: 'base' })
  );
};

export const getClientTransactionLabel = (transaction = {}) =>
  normalizeClientName(transaction.clientName || transaction.customerName || '');

export const normalizeClientAllocations = (allocations = [], clientsById = new Map()) => {
  const source = Array.isArray(allocations) ? allocations : [];
  const uniqueAllocations = new Map();

  source.forEach((allocation) => {
    const clientId = normalizeString(allocation?.clientId || allocation?.id);
    if (!clientId || uniqueAllocations.has(clientId)) return;

    const linkedClient = clientsById instanceof Map ? clientsById.get(clientId) : null;
    const clientName = linkedClient
      ? getClientDisplayName(linkedClient)
      : normalizeClientName(allocation?.clientName || allocation?.name || '');

    uniqueAllocations.set(clientId, {
      clientId,
      clientName
    });
  });

  const normalized = [...uniqueAllocations.values()];
  if (normalized.length === 0) return [];

  const equalPercent = 1 / normalized.length;
  return normalized.map((allocation) => ({
    ...allocation,
    allocationPercent: equalPercent
  }));
};

export const getTransactionClientAllocations = (transaction = {}, clientsById = new Map(), totalAmount = null) => {
  const explicitAllocations = normalizeClientAllocations(transaction.clientAllocations, clientsById);
  const allocations = explicitAllocations.length > 0
    ? explicitAllocations
    : normalizeClientAllocations(
        transaction.clientId
          ? [{ clientId: transaction.clientId, clientName: transaction.clientName }]
          : [],
        clientsById
      );

  const amount = Number(totalAmount ?? transaction.expenseTotalAmount ?? transaction.amount) || 0;
  let allocatedTotal = 0;

  return allocations.map((allocation, index) => {
    const isLast = index === allocations.length - 1;
    const allocatedAmount = isLast
      ? amount - allocatedTotal
      : parseFloat((amount * allocation.allocationPercent).toFixed(2));
    allocatedTotal += allocatedAmount;

    return {
      ...allocation,
      allocatedAmount: parseFloat((allocatedAmount || 0).toFixed(2))
    };
  });
};

export const getClientAllocationLabel = (transaction = {}, clientsById = new Map()) => {
  const allocations = getTransactionClientAllocations(transaction, clientsById, 0);
  if (allocations.length === 0) return '';
  if (allocations.length === 1) return allocations[0].clientName || 'Linked client';
  const names = allocations.map((allocation) => allocation.clientName || 'Linked client');
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
};
